// FIRESTORE FILTERING NOTES:
// - Text search (name, email, refId) is client-side only; filters the current page.
// - Status, position, and templateID filters are server-side.
// - Changing any server-side filter resets pagination to page 1.
// - OVERDUE maps to SessionStatus.Expired backend-side; time-based overdue for
//   NOT_STARTED/IN_PROGRESS sessions cannot be filtered server-side.

import { useMemo, useState } from 'react';

import { FaceFrownIcon } from '@heroicons/react/24/outline';

import { useGetAllSessionsQuery, useGetSessionsStatsQuery } from '@/domains/session';
import { useGetJobPositionsQuery } from '@/domains/position';
import { useGetActiveTemplatesQuery } from '@/domains/template';
import { SessionStatus } from '@/libs/graphql';
import { useBackofficeContext } from '@/providers/Backoffice';
import { useCursorPagination } from '@/utils/useCursorPagination';

import CandidateSessionDataTable from './components/CandidateSessionDataTable';
import FilterBar from './components/FilterBar';
import StatsCards from './components/StatsCards';
import { CandidateDashboardFilters } from './types';

const PAGE_SIZE = 50;

const INITIAL_FILTERS: CandidateDashboardFilters = {
  search: '',
  status: null,
  position: null,
  questionTemplateID: null,
};

const CandidateDashboard = () => {
  const { loading: userLoading } = useBackofficeContext();
  const [filters, setFilters] = useState<CandidateDashboardFilters>(INITIAL_FILTERS);

  const { currentCursor, currentPage, hasPrevPage, pushCursor, goToPrevPage, resetCursors } =
    useCursorPagination();

  const { data: statsData, loading: statsLoading } = useGetSessionsStatsQuery({
    fetchPolicy: 'cache-and-network',
  });

  const { data: allSessionsData, loading: sessionsLoading } = useGetAllSessionsQuery({
    variables: {
      input: {
        limit: PAGE_SIZE,
        cursor: currentCursor,
        status: filters.status ?? undefined,
        position: filters.position ?? undefined,
        questionTemplateID: filters.questionTemplateID ?? undefined,
      },
    },
    fetchPolicy: 'network-only',
  });

  const sessions = allSessionsData?.getAllSessions?.sessions;
  const total = allSessionsData?.getAllSessions?.total ?? 0;
  const hasNextPage = (sessions?.length ?? 0) === PAGE_SIZE;

  const goToNextPage = () => {
    const lastSession = sessions?.[sessions.length - 1];
    if (lastSession?.createdAt && lastSession?.id) {
      pushCursor({ createdAt: lastSession.createdAt as string, id: lastSession.id as string });
    }
  };

  const { data: positionsData } = useGetJobPositionsQuery();
  const { data: templatesData } = useGetActiveTemplatesQuery();

  const filteredSessions = useMemo(() => {
    if (!sessions) return sessions;
    const q = filters.search.trim().toLowerCase();
    if (!q) return sessions;
    return sessions.filter(
      (s) =>
        s.intervieweeName?.toLowerCase().includes(q) ||
        s.intervieweeEmail?.toLowerCase().includes(q) ||
        s.refId?.toLowerCase().includes(q),
    );
  }, [sessions, filters.search]);

  const handleFilterChange = (newFilters: CandidateDashboardFilters) => {
    const serverSideChanged =
      newFilters.status !== filters.status ||
      newFilters.position !== filters.position ||
      newFilters.questionTemplateID !== filters.questionTemplateID;
    if (serverSideChanged) {
      resetCursors();
    }
    setFilters(newFilters);
  };

  const handleStatusClick = (status: SessionStatus | null) => {
    handleFilterChange({ ...filters, status });
  };

  const positionOptions = useMemo(
    () => (positionsData?.getJobPositions ?? []).map((p) => ({ value: p.title, label: p.title })),
    [positionsData],
  );

  const templateOptions = useMemo(
    () =>
      (templatesData?.getActiveTemplates ?? []).map((t) => ({
        value: t.id,
        label: t.name,
      })),
    [templatesData],
  );

  const isLoading = userLoading || sessionsLoading;

  return (
    <div tw="m-auto flex min-h-screen flex-col space-y-5 px-4 py-6 lg:(px-20 py-14)">
      <h3>ติดตามผู้สมัคร</h3>
      <StatsCards
        data={statsData?.getSessionsStats}
        loading={statsLoading}
        activeStatus={filters.status}
        onStatusClick={handleStatusClick}
      />
      <FilterBar
        filters={filters}
        onChange={handleFilterChange}
        positions={positionOptions}
        templates={templateOptions}
      />
      {isLoading ? (
        <div tw="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm animate-pulse">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} tw="px-4 py-4 border-b border-gray-100 flex gap-6">
              <div tw="flex-1 space-y-2">
                <div tw="h-4 bg-gray-100 rounded w-32" />
                <div tw="h-3 bg-gray-100 rounded w-48" />
              </div>
              <div tw="flex-1 space-y-2">
                <div tw="h-4 bg-gray-100 rounded w-24" />
                <div tw="h-3 bg-gray-100 rounded w-32" />
              </div>
              <div tw="w-24 h-6 bg-gray-100 rounded-full" />
              <div tw="w-20 h-4 bg-gray-100 rounded" />
              <div tw="w-10 h-4 bg-gray-100 rounded" />
              <div tw="w-28 h-8 bg-gray-100 rounded-xl" />
            </div>
          ))}
        </div>
      ) : Array.isArray(filteredSessions) && filteredSessions.length === 0 ? (
        <div tw="flex w-full flex-col items-center justify-center gap-2 rounded-2xl bg-white border border-gray-200 p-14">
          <FaceFrownIcon tw="h-6 w-6 text-gray-400" />
          <h5 tw="text-gray-500">ไม่พบการสัมภาษณ์</h5>
        </div>
      ) : (
        <CandidateSessionDataTable
          sessions={filteredSessions}
          total={total}
          currentPage={currentPage}
          pageSize={PAGE_SIZE}
          hasNextPage={hasNextPage}
          hasPrevPage={hasPrevPage}
          onNext={goToNextPage}
          onPrev={goToPrevPage}
          onReset={resetCursors}
        />
      )}
    </div>
  );
};

export default CandidateDashboard;
